import React, { useEffect, useRef } from "react";
import { Copy, X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { buildSocialLinks, copyShareBlob } from "@/lib/share";

/**
 * Dropdown popup with one-click share links for WhatsApp / Telegram / X / FB
 * plus a "copy" fallback. Used as the desktop fallback when the Web Share API
 * isn't supported. Pass `text` (the formatted message body) and `url`.
 */
export default function SharePopupMenu({ text, url, title = "Share", onClose, testidPrefix = "share-status" }) {
    const { t } = useLang();
    const ref = useRef(null);
    const links = buildSocialLinks({ text, url });

    useEffect(() => {
        const onDown = (e) => {
            if (!ref.current) return;
            if (ref.current.contains(e.target)) return;
            onClose?.();
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [onClose]);

    const onCopy = async () => {
        const ok = await copyShareBlob(text, url);
        if (ok) toast.success(t("reports.shareCopied"));
        onClose?.();
    };

    return (
        <div
            ref={ref}
            data-testid={`${testidPrefix}-popup`}
            className="absolute right-0 mt-2 w-72 bg-zinc-950 border border-white/15 shadow-2xl z-50"
        >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
                <span className="text-[10px] tracking-[0.3em] uppercase text-abnormal-acid font-bold truncate">{title}</span>
                <button onClick={onClose} className="text-zinc-500 hover:text-white" aria-label="close"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-2 gap-px bg-white/5">
                <a href={links.whatsapp} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-wa`}
                   onClick={onClose}
                   className="bg-zinc-950 hover:bg-abnormal-acid hover:text-black px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">WhatsApp</a>
                <a href={links.telegram} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-tg`}
                   onClick={onClose}
                   className="bg-zinc-950 hover:bg-abnormal-acid hover:text-black px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">Telegram</a>
                <a href={links.twitter} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-x`}
                   onClick={onClose}
                   className="bg-zinc-950 hover:bg-abnormal-acid hover:text-black px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">X / Twitter</a>
                <a href={links.facebook} target="_blank" rel="noreferrer" data-testid={`${testidPrefix}-fb`}
                   onClick={onClose}
                   className="bg-zinc-950 hover:bg-abnormal-acid hover:text-black px-4 py-3 text-xs uppercase tracking-wider font-bold transition-colors">Facebook</a>
            </div>
            <button
                type="button"
                onClick={onCopy}
                data-testid={`${testidPrefix}-copy`}
                className="w-full flex items-center justify-between px-4 py-3 border-t border-white/10 text-xs uppercase tracking-wider font-bold hover:bg-white/5 transition-colors"
            >
                <span>{t("common.copyLink") || "Copia"}</span>
                <Copy size={14} />
            </button>
        </div>
    );
}
